import React from "react";
import { ArrowUpRight, CalendarClock, FileText } from "lucide-react";

import TemplateRow from "./TemplateRow";
import TemplateStatusBadge from "./TemplateStatusBadge";

const qualityConfig = {
  GREEN: { label: "High", dot: "bg-[#20a777]" },
  HIGH: { label: "High", dot: "bg-[#20a777]" },
  YELLOW: { label: "Medium", dot: "bg-[#d49542]" },
  MEDIUM: { label: "Medium", dot: "bg-[#d49542]" },
  RED: { label: "Low", dot: "bg-[#c8645f]" },
  LOW: { label: "Low", dot: "bg-[#c8645f]" },
};

function QualityLabel({ value }) {
  const quality = qualityConfig[String(value || "").toUpperCase()] || {
    label: "Not rated",
    dot: "bg-[#a2b0b7]",
  };

  return (
    <span className="inline-flex items-center gap-1.5 text-[9px] font-bold text-[#607987] dark:text-[#91a9b6]">
      <span className={`h-1.5 w-1.5 rounded-full ${quality.dot}`} />
      {quality.label}
    </span>
  );
}

function CardView({ templates, onPreview }) {
  return (
    <div className="grid gap-4 p-4 sm:p-5 md:grid-cols-2 2xl:grid-cols-3">
      {templates.map((template) => (
        <TemplateRow
          key={template.id}
          template={template}
          onPreview={onPreview}
        />
      ))}
    </div>
  );
}

function TableView({ templates, onPreview }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[940px] border-collapse">
        <thead>
          <tr className="border-b border-[#dce8ed] bg-[#f6fafb]/85 dark:border-[#29404d] dark:bg-[#132630]/85">
            {[
              ["Template", "text-left"],
              ["Category", "text-left"],
              ["Language", "text-left"],
              ["Status", "text-left"],
              ["Quality", "text-left"],
              ["Updated", "text-left"],
              ["Preview", "text-right"],
            ].map(([label, align]) => (
              <th key={label} scope="col" className={`px-5 py-3.5 text-[8px] font-bold uppercase tracking-[0.14em] text-[#738995] dark:text-[#78919e] ${align}`}>
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#e3ecef] dark:divide-[#29404d]">
          {templates.map((template) => (
            <tr key={template.id} className="group bg-[#ffffff]/80 transition-colors hover:bg-[#f7fbfc] dark:bg-[#11232d]/74 dark:hover:bg-[#162d38]">
              <td className="px-5 py-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#b9dff0] bg-[#edf8fd] text-[#147db5] dark:border-[#28516a] dark:bg-[#142a36] dark:text-[#6fc5eb]">
                    <FileText size={15} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0 max-w-[280px]">
                    <p className="truncate text-[11px] font-bold text-[#18364a] dark:text-[#eaf3f7]" title={template.name}>{template.name}</p>
                    <p className="mt-1 truncate text-[8px] font-medium text-[#91a0a8] dark:text-[#6f8996]">ID · {template.id}</p>
                  </div>
                </div>
              </td>
              <td className="px-5 py-4 text-[9px] font-bold text-[#536e7d] dark:text-[#9ab0bb]">{template.category || "—"}</td>
              <td className="px-5 py-4 text-[9px] font-semibold text-[#6d8390] dark:text-[#849daa]">{template.language || "—"}</td>
              <td className="px-5 py-4"><TemplateStatusBadge status={template.status} /></td>
              <td className="px-5 py-4"><QualityLabel value={template.quality} /></td>
              <td className="px-5 py-4">
                <span className="inline-flex items-center gap-1.5 text-[9px] font-semibold text-[#728793] dark:text-[#8099a6]">
                  <CalendarClock size={11} />
                  {template.updatedAt || "—"}
                </span>
              </td>
              <td className="px-5 py-4 text-right">
                <button
                  type="button"
                  onClick={() => onPreview?.(template)}
                  title="View template"
                  aria-label={`Preview ${template.name}`}
                  className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-[#c7dce5] bg-white px-2.5 text-[8px] font-bold text-[#147db5] transition-all hover:border-[#8fc5da] hover:bg-[#edf8fd] dark:border-[#315364] dark:bg-[#162d38] dark:text-[#6fc5eb]"
                >
                  Open
                  <ArrowUpRight size={10} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const TemplateTable = ({ templates, onPreview, viewMode = "cards" }) => {
  if (viewMode === "table") {
    return <TableView templates={templates} onPreview={onPreview} />;
  }

  return <CardView templates={templates} onPreview={onPreview} />;
};

export default TemplateTable;
